import type { KeyboardEvent } from 'react';
import type { CodeBlock } from '@atlas/renderer';
import { Editor, Element, Transforms } from 'slate';
import matchNode from '@atlas/tool/box/slate/matchNode';
import sortSelection from '@atlas/tool/box/slate/sortSelection';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function handleCodeBlockIndentationKeyDownEvent(editor: Editor, event: KeyboardEvent): boolean {
    if (!editor.selection) return false;

    if (event.key !== 'Tab') return false;
    if (event.altKey || event.metaKey || event.ctrlKey) return false;

    const { anchor, focus } = editor.selection;
    if (anchor.path[0] !== focus.path[0]) return false;

    const node = matchNode<CodeBlock>(editor, (node) => Element.isElement(node) && node.type === 'code-block');
    if (!node) return false;

    event.preventDefault();

    const noSelection = anchor.offset === focus.offset && anchor.path.every((p, i) => p === focus.path[i]);

    const back = event.shiftKey;

    if (noSelection) {
        if (!back) {
            Transforms.insertText(editor, '\t');
            return true;
        }

        const currentLineBeginning = Editor.string(editor, {
            anchor: Editor.start(editor, [anchor.path[0]]),
            focus,
        })
            .split('\n')
            .pop() as string;

        const currentLineEnding = Editor.string(editor, {
            anchor,
            focus: Editor.end(editor, [focus.path[0]]),
        })
            .split('\n')
            .shift() as string;

        const currentLine = currentLineBeginning + currentLineEnding;
        if (!currentLine.startsWith('\t')) return true;

        const selectedNodeText = node.children[anchor.path[1]].text;

        for (let i = anchor.path[1]; i >= 0; i--) {
            const leaf = node.children[i];
            const lastLineBreak = (i === anchor.path[1] ? leaf.text.slice(0, anchor.offset) : leaf.text).lastIndexOf(
                '\n',
            );
            if (i !== 0 && lastLineBreak === -1) continue;

            const text = leaf.text.slice(0, lastLineBreak + 1) + leaf.text.slice(lastLineBreak + 2);

            Transforms.removeNodes(editor, {
                at: [anchor.path[0]],
            });
            const children = node.children.map((c, index) => (index === i ? { ...c, text } : c));
            Transforms.insertNodes(
                editor,
                {
                    ...node,
                    children,
                },
                { at: [anchor.path[0]] },
            );
            if (anchor.offset && selectedNodeText !== children[anchor.path[1]].text) {
                const position = {
                    offset: currentLineBeginning.startsWith('\t') ? anchor.offset - 1 : anchor.offset,
                    path: anchor.path,
                };
                changeEditorSelection(editor, { anchor: position, focus: position });
            } else {
                changeEditorSelection(editor, { anchor, focus });
            }
            break;
        }

        return true;
    }

    const [start, end] = sortSelection(editor.selection);

    if (!back) {
        const children = [...node.children];
        for (let i = end.path[1]; i >= 0; i--) {
            const leaf = node.children[i];
            if (i < start.path[1]) {
                const lastLineBreak = leaf.text.lastIndexOf('\n');
                if (lastLineBreak === -1) continue;
                children[i] = {
                    ...leaf,
                    text: leaf.text.slice(0, lastLineBreak + 1) + '\t' + leaf.text.slice(lastLineBreak + 1),
                };
                break;
            } else if (i === start.path[1]) {
                if (i !== 0 && !leaf.text.includes('\n')) continue;

                const text = leaf.text.slice(0, start.offset) + leaf.text.slice(start.offset).replace(/\n/g, '\n\t');
                const lineBreakBeforeSelection = leaf.text.slice(0, start.offset).lastIndexOf('\n');

                if (i === 0) {
                    children[0] = {
                        ...leaf,
                        text:
                            lineBreakBeforeSelection === -1
                                ? '\t' + text
                                : text.slice(0, lineBreakBeforeSelection + 1) +
                                  '\t' +
                                  text.slice(lineBreakBeforeSelection + 1),
                    };
                } else if (lineBreakBeforeSelection !== -1) {
                    children[i] = {
                        ...leaf,
                        text:
                            text.slice(0, lineBreakBeforeSelection) +
                            text.slice(lineBreakBeforeSelection).replace(/\n/g, '\n\t'),
                    };
                    break;
                } else {
                    children[i] = { ...leaf, text };
                }
            } else {
                children[i] = { ...leaf, text: leaf.text.replace(/\n/g, '\n\t') };
            }
        }

        Transforms.removeNodes(editor, {
            at: [start.path[0]],
        });

        Transforms.insertNodes(
            editor,
            {
                ...node,
                children,
            },
            { at: [start.path[0]] },
        );

        const startShift =
            node.children[start.path[1]].text.slice(0, start.offset) !==
            children[start.path[1]].text.slice(0, start.offset)
                ? 1
                : 0;

        const endShift =
            node.children[end.path[1]].text
                .slice(start.path[1] === end.path[1] ? start.offset : 0, end.offset)
                .split('\n').length -
            1 +
            (start.path[1] === end.path[1] ? startShift : 0);

        const newStart = {
            offset: start.offset + startShift,
            path: start.path,
        };

        const newEnd = {
            offset: end.offset + endShift,
            path: end.path,
        };

        changeEditorSelection(editor, {
            anchor: anchor === start ? newStart : newEnd,
            focus: focus === start ? newStart : newEnd,
        });

        return true;
    }

    // Shift tab selection

    const children = [...node.children];
    for (let i = end.path[1]; i >= 0; i--) {
        const leaf = node.children[i];
        if (i < start.path[1]) {
            const lastLineBreak = leaf.text.lastIndexOf('\n');
            if (lastLineBreak === -1) continue;
            if (leaf.text[lastLineBreak + 1] !== '\t') break;
            children[i] = {
                ...leaf,
                text: leaf.text.slice(0, lastLineBreak + 1) + leaf.text.slice(lastLineBreak + 2),
            };
            break;
        } else if (i === start.path[1]) {
            if (i !== 0 && !leaf.text.includes('\n')) continue;

            const text = leaf.text.slice(0, start.offset) + leaf.text.slice(start.offset).replace(/\n\t/g, '\n');
            const lineBreakBeforeSelection = leaf.text.slice(0, start.offset).lastIndexOf('\n');

            if (i === 0) {
                if (leaf.text[lineBreakBeforeSelection + 1] !== '\t') break;

                children[0] = {
                    ...leaf,
                    text:
                        lineBreakBeforeSelection === -1
                            ? text.slice(1)
                            : text.slice(0, lineBreakBeforeSelection + 1) + text.slice(lineBreakBeforeSelection + 2),
                };
            } else if (lineBreakBeforeSelection !== -1) {
                children[i] = {
                    ...leaf,
                    text:
                        text.slice(0, lineBreakBeforeSelection) +
                        text.slice(lineBreakBeforeSelection).replace(/\n\t/g, '\n'),
                };
                break;
            } else {
                children[i] = { ...leaf, text };
            }
        } else {
            children[i] = { ...leaf, text: leaf.text.replace(/\n\t/g, '\n') };
        }
    }

    Transforms.removeNodes(editor, {
        at: [start.path[0]],
    });

    Transforms.insertNodes(
        editor,
        {
            ...node,
            children,
        },
        { at: [start.path[0]] },
    );

    const startShift =
        start.offset &&
        node.children[start.path[1]].text.slice(0, start.offset) !== children[start.path[1]].text.slice(0, start.offset)
            ? 1
            : 0;

    const endShift =
        node.children[end.path[1]].text
            .slice(start.path[1] === end.path[1] ? start.offset : 0, end.offset)
            .split('\n\t').length -
        1 +
        (start.path[1] === end.path[1] ? startShift : 0);

    const newStart = {
        offset: start.offset - startShift,
        path: start.path,
    };

    const newEnd = {
        offset: end.offset - endShift,
        path: end.path,
    };

    changeEditorSelection(editor, {
        anchor: anchor === start ? newStart : newEnd,
        focus: focus === start ? newStart : newEnd,
    });

    return true;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleCodeBlockIndentationKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
